import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { CirclePicker } from 'react-color';
import { FaArrowLeft, FaTag, FaPalette, FaSave, FaSpinner } from 'react-icons/fa';
import { createLeadStatus, getLeadStatusById, updateLeadStatus } from './leadStatusService';
import DashboardLayout from '../../layouts/DashboardLayout';
import Input from '../../components/shared/Input';
import {
  FormContainer,
  FormSection,
  FormHeader,
  BackButton,
  FormGrid,
  FormField,
  InputWrapper,
  ErrorMessage,
  SubmitButton
} from '../../components/styled/FormComponents';
import styled from 'styled-components';

const ColorPickerWrapper = styled(FormField)`
  .circle-picker {
    width: 100% !important;
    justify-content: center;
    margin-top: ${props => props.theme.spacing(2)};
    
    > span {
      margin: ${props => props.theme.spacing(1)} !important;
    }

    > span > div {
      width: 40px !important;
      height: 40px !important;
      
      &:hover {
        transform: scale(1.1);
      }
    }
  }
`;

const ColorPreview = styled.div`
  width: 100%;
  height: 60px;
  border-radius: ${props => props.theme.borderRadius};
  background-color: ${props => props.color};
  margin-top: ${props => props.theme.spacing(2)};
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: ${props => props.theme.typography.fontSize.small};
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
`;

const defaultColors = [
  '#ff1744', '#f50057', '#d500f9', '#3d5afe', '#2979ff',
  '#1de9b6', '#00e676', '#ffea00', '#ff9100', '#9e9e9e'
];

const LeadStatusForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    status_name: '',
    color: '#3498db'
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchLeadStatus = async () => {
      if (!id) return;
      
      try {
        setLoading(true);
        const response = await getLeadStatusById(id);
        setFormData({
          status_name: response.data.status_name,
          color: response.data.color
        });
      } catch (err) {
        setError('Failed to load lead status');
        console.error('Error fetching lead status:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchLeadStatus();
  }, [id]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      if (id) {
        await updateLeadStatus(id, formData);
      } else {
        await createLeadStatus(formData);
      }
      navigate('/leads/status');
    } catch (err) {
      setError('Failed to save lead status');
      console.error('Error saving lead status:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <DashboardLayout>
      <FormContainer>
        <FormHeader>
          <BackButton onClick={() => navigate('/leads/status')}>
            <FaArrowLeft /> Back to Lead Status
          </BackButton>
          <h2>{id ? 'Edit Lead Status' : 'New Lead Status'}</h2>
        </FormHeader>

        <form onSubmit={handleSubmit}>
          <FormSection>
            <h3>Status Information</h3>
            <FormGrid>
              <FormField index={0}>
                <Input
                  label="Status Name"
                  icon={<FaTag />}
                  value={formData.status_name}
                  onChange={(e) => setFormData({
                    ...formData,
                    status_name: e.target.value
                  })}
                  placeholder="Enter status name"
                  required
                />
              </FormField>

              <ColorPickerWrapper index={1}>
                <Input
                  label="Status Color"
                  icon={<FaPalette />}
                  value={formData.color}
                  onChange={(e) => setFormData({
                    ...formData,
                    color: e.target.value
                  })}
                  disabled
                />
                <ColorPreview color={formData.color}>
                  {formData.color}
                </ColorPreview>
                <CirclePicker
                  color={formData.color}
                  onChangeComplete={(color) => setFormData({
                    ...formData,
                    color: color.hex
                  })}
                  colors={defaultColors}
                />
              </ColorPickerWrapper>
            </FormGrid>

            {error && <ErrorMessage>{error}</ErrorMessage>}
          </FormSection>

          <SubmitButton type="submit" disabled={loading}>
            {loading ? (
              <>
                <FaSpinner className="icon-spin" />
                {id ? 'Updating...' : 'Creating...'}
              </>
            ) : (
              <>
                <FaSave />
                {id ? 'Update Status' : 'Create Status'}
              </>
            )}
          </SubmitButton>
        </form>
      </FormContainer>
    </DashboardLayout>
  );
};

export default LeadStatusForm;

// // src/pages/LeadStatusForm.js
// import React, { useState, useEffect } from 'react';
// import { useNavigate, useParams } from 'react-router-dom';
// import { CirclePicker } from 'react-color'; // Color picker component
// import { createLeadStatus, getLeadStatusById, updateLeadStatus } from './leadStatusService';
// import DashboardLayout from '../../layouts/DashboardLayout';
// import Button from '../../components/shared/Button';
// import styled from 'styled-components';
// import { BackButton, FormHeader } from '../../components/styled/FormComponents';
// import { FaArrowLeft } from 'react-icons/fa';

// const FormWrapper = styled.div`
//   max-width: 600px;
//   margin: 50px auto;
//   padding: 40px;
//   background-color: ${(props) => props.theme.colors.surface};
//   border-radius: 12px;
//   box-shadow: 0 4px 10px rgba(0, 0, 0, 0.15);
// `;

// const Input = styled.input`
//   width: 100%;
//   padding: 10px;
//   margin-bottom: 20px;
//   border: 1px solid ${(props) => props.theme.colors.inputBorder};
//   border-radius: 5px;
// `;

// const SectionTitle = styled.h2`
//   font-size: 24px;
//   margin-bottom: ${(props) => props.theme.spacing(4)};
//   text-align: center;
//   color: ${(props) => props.theme.colors.primaryText};
// `;

// const defaultColors = [
//     '#ff1744', // Red
//     '#f50057', // Pink
//     '#d500f9', // Purple
//     '#3d5afe', // Indigo
//     '#2979ff', // Blue
//     '#1de9b6', // Teal
//     '#00e676', // Green
//     '#ffea00', // Yellow
//     '#ff9100', // Orange
//     '#9e9e9e', // Gray
//   ];

// const LeadStatusForm = () => {
//   const { id } = useParams();
//   const navigate = useNavigate();
//   const [statusName, setStatusName] = useState('');
//   const [color, setColor] = useState('#3498db');
//   const [isEditMode, setIsEditMode] = useState(false);

//   useEffect(() => {
//     if (id) {
//       const fetchLeadStatus = async () => {
//         const response = await getLeadStatusById(id);
//         setStatusName(response.data.status_name);
//         setColor(response.data.color);
//         setIsEditMode(true);
//       };
//       fetchLeadStatus();
//     }
//   }, [id]);

//   const handleSubmit = async (e) => {
//     e.preventDefault();
//     const statusData = { status_name: statusName, color };

//     if (isEditMode) {
//       await updateLeadStatus(id, statusData);
//     } else {
//       await createLeadStatus(statusData);
//     }

//     navigate('/leads/status');
//   };

//   return (
//     <DashboardLayout>
//       <FormHeader>
//           <BackButton onClick={() => navigate('/leads/status')}>
//             <FaArrowLeft /> Back to Lead Status
//           </BackButton>
//           <h2>{id ? 'Edit Lead Status' : 'New Lead Status'}</h2>
//         </FormHeader>

//       <FormWrapper>
//         <SectionTitle>{isEditMode ? 'Edit' : 'Add'} Lead Status</SectionTitle>
//         <form onSubmit={handleSubmit}>
//           <Input
//             type="text"
//             placeholder="Enter status name"
//             value={statusName}
//             onChange={(e) => setStatusName(e.target.value)}
//             required
//           />

//           <CirclePicker
//             color={color}
//             onChangeComplete={(color) => setColor(color.hex)}
//             colors={defaultColors}
//           />

//           <Button type="submit" style={{ width: '100%', marginTop: '20px' }}>
//             {isEditMode ? 'Update' : 'Create'} Lead Status
//           </Button>
//         </form>
//       </FormWrapper>
//     </DashboardLayout>
//   );
// };

// export default LeadStatusForm;
